import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { buildGame } from '../api/gameBuilderAPI';
import MissionGame from './MissionGame';
import MissionTransition from './MissionTransition';
import { playableChain } from './chain';
import './StorylinePlayer.css';

/**
 * Plays a plan's storyline (backend plan.save_storyline): its missions in order. Winning one plays
 * the link into the next - a time skip or a first-person walk to the next room (MissionTransition) -
 * and the next mission opens on its own briefing. Every mission's game is already built (the editor
 * only offers game-ready missions), so loading is quick and makes no AI calls.
 */
export default function StorylinePlayer({ plan, onClose, page = false }) {   // page: in its own window (GamePages.jsx)
  const { missions: order, links } = playableChain(plan);          // its painted games (one being rebuilt is skipped)
  const names = Object.fromEntries(plan.missions.map((m) => [m.id, m.name]));
  const [specs, setSpecs] = useState(null);
  const [loaded, setLoaded] = useState(0);
  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);
  const [moving, setMoving] = useState(false);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = [];
        for (const id of order) {
          out.push(await buildGame(plan.id, id));
          if (cancelled) return;
          setLoaded(out.length);
        }
        setSpecs(out);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [plan.id, order]);

  const last = index === order.length - 1;

  if (error || !specs) {
    return createPortal(
      <div className={`sp-backdrop ${page ? 'is-page' : ''}`} role="dialog" aria-modal="true">
        <div className="sp-card">
          <h3>{error ? "The storyline couldn't start" : 'Getting the storyline ready…'}</h3>
          {error ? <p>{error}</p> : <p><span className="sp-spinner" /> Mission {Math.min(loaded + 1, order.length)} of {order.length}</p>}
          <button type="button" className="sp-btn sp-btn-quiet" onClick={onClose}>Close</button>
        </div>
      </div>,
      document.body
    );
  }

  if (complete) {
    return createPortal(
      <div className={`sp-backdrop ${page ? 'is-page' : ''}`} role="dialog" aria-modal="true">
        <div className="sp-card">
          <h3>Storyline complete</h3>
          <ol className="sp-chain">
            {order.map((id, i) => (
              <li key={id}>
                <strong>{names[id]}</strong>
                {i < links.length && <span className="sp-link">{links[i].type === 'place' ? '🚶' : '🕐'} {links[i].label}</span>}
              </li>
            ))}
          </ol>
          <div className="sp-actions">
            <button type="button" className="sp-btn" onClick={() => { setIndex(0); setComplete(false); }}>Play again</button>
            <button type="button" className="sp-btn sp-btn-quiet" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <>
      <MissionGame
        page={page}
        key={`${index}`}
        spec={specs[index]}
        chapter={`${index + 1} / ${order.length}`}
        onClose={onClose}
        winLabel={last ? 'finish the storyline' : links[index].label || names[order[index + 1]]}
        onWin={() => (last ? setComplete(true) : setMoving(true))}
      />
      {moving && (
        <MissionTransition
          link={links[index]}
          fromSrc={specs[index].background_src}
          toSrc={specs[index + 1].background_src}
          nextName={names[order[index + 1]]}
          onDone={() => { setMoving(false); setIndex((i) => i + 1); }}
        />
      )}
    </>
  );
}
